import React, { useState } from 'react';
import { Menu, Label, Icon, Message, Dropdown} from 'semantic-ui-react';


/**
 * =========================================
 * 1. FILTER SCHEMA (SOURCE OF TRUTH)
 * =========================================
 */
const FILTER_SCHEMA = {
  gender: [
    { value: "m", label: "Male" },
    { value: "f", label: "Female" }
  ],

  composer_gender: [
    { value: "m", label: "Male" },
    { value: "f", label: "Female" }
  ],

  language: [
    { value: "english", label: "English" },
    { value: "spanish", label: "Spanish" },
    { value: "italian", label: "Italian" },
    { value: "french", label: "French" },
    { value: "german", label: "German" },
    { value: "hungarian", label: "Hungarian" },
    { value: "portuguese", label: "Portuguese" },
    { value: "russian", label: "Russian" }
  ], 

  voice_type: [
    { value: "1", label: "Baritone" },
    { value: "2", label: "Bass" },
    { value: "3", label: "Bass-Baritone" },
    { value: "4", label: "Bass-Barritone" },
    { value: "5", label: "Contralto" },
    { value: "6", label: "Female Voice" },
    { value: "7", label: "High Voice" },
    { value: "8", label: "Low Voice" },
    { value: "9", label: "Male Voice" },
    { value: "10", label: "Medium-High Voice" },
    { value: "11", label: "Medium Voice" },
    { value: "12", label: "Mezzo-Soprano" },
    { value: "13", label: "Not Indicated" },
    { value: "14", label: "Soprano" },
    { value: "15", label: "Spoken Female Voice" },
    { value: "16", label: "Spoken Voice" },
    { value: "17", label: "Voice" },
    { value: "18", label: "Tenor" },
    { value: "19", label: "Boy Soprano" },
    { value: "20", label: "Countertenor" },
    { value: "21", label: "Medium-Low Voice" },
    { value: "22", label: "Alto" },
    { value: "23", label: "Treble Voice" }
  ]

};

/**
 * =========================================
 * 2. TOGGLE MULTI-SELECT VALUES
 * =========================================
 */
function toggleValue(prev = {}, key, value) {
  const current = Array.isArray(prev[key]) ? [...prev[key]] : [];

  const exists = current.includes(value);

  const nextValues = exists
    ? current.filter(v => v !== value)
    : [...current, value];

  return {
    ...prev,
    [key]: nextValues.length ? nextValues : undefined
  };
}

/**
 * =========================================
 * 3. DATE HELPERS
 * =========================================
 */
function toYear(v) {
  if (!v && v !== 0) return null;
  const d = new Date(v);
  const y = d.getUTCFullYear();
  return isNaN(y) ? null : y;
}

function getRange(buckets = []) {
  const years = buckets
    .map(b => toYear(b.key_as_string || b.key))
    .filter(Boolean);

  if (!years.length) return [1800, 2026];

  return [Math.min(...years), Math.max(...years)];
}

function isDateHistogram(buckets = []) {
  if (!buckets.length) return false;
  return !isNaN(toYear(buckets[0].key_as_string || buckets[0].key));
}

/**
 * =========================================
 * 4. SERIALIZER (🔥 FIX FOR YOUR BUG)
 * =========================================
 * Keeps backend format: "1700-1850"
 */
export function buildQueryParams(optAggs = {}) {
  const params = new URLSearchParams();

  Object.entries(optAggs).forEach(([key, value]) => {

    // DATE RANGE → "1700-1850"
    if (
      value &&
      typeof value === "object" &&
      value.gte != null &&
      value.lte != null
    ) {
      params.append(key, `${value.gte}-${value.lte}`);
      return;
    }

    // ARRAY FILTERS (categorical)
    if (Array.isArray(value)) {
      value.forEach(v => params.append(key, v));
      return;
    }

    // fallback
    params.append(key, value);
  });

  return params;
}

/**
 * =========================================
 * 5. MAIN COMPONENT
 * =========================================
 */
export const Filters = ({ aggs, optAggs, setOptAggs, filterError }) => {
  const [openSections, setOpenSections] = useState({});

  if (!aggs) return null;

  const getBucketMap = (buckets = []) => {
    const map = new Map();

    if (Array.isArray(buckets)) {
      buckets.forEach(b => {
        map.set(b.key_as_string ?? b.key, b.doc_count);
      });
    } else {
      Object.entries(buckets).forEach(([key, bucket]) => {
        map.set(key, bucket.doc_count);
      });
    }

    return map;
  };

  const toggleSection = (name) => {
    setOpenSections(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const sections = Object.keys(aggs)
    .map(k => ({
      key: k,
      name: k.replace(/\W+|_+/g, ' ').trim(),
      data: aggs[k]
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Menu vertical secondary fluid>
      <Menu.Item header>Filters</Menu.Item>

      {sections.map((section, i) => {

        const fieldKey = section.name
          .toLowerCase()
          .replace(/\s+/g, '_');

        const buckets = section.data?.buckets || [];
        const schema = FILTER_SCHEMA[fieldKey];
        const isOpen = openSections[section.name];

        const bucketMap = getBucketMap(buckets);

        /**
         * =========================
         * DATE RANGE MODE (INPUT BOXES)
         * =========================
         */
        if (!schema && isDateHistogram(buckets)) {

          const range = getRange(buckets);

          const active = optAggs?.[fieldKey]?.gte != null
            ? {
                gte: optAggs[fieldKey].gte,
                lte: optAggs[fieldKey].lte
              }
            : {
                gte: range[0],
                lte: range[1]
              };

          return (
            <Menu.Item key={i}>
              <div
                onClick={() => toggleSection(section.name)}
                style={{
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                {section.name}
                <Icon name={isOpen ? "chevron up" : "chevron down"} />
              </div>

              {isOpen && (
                <div style={{ padding: "10px 5px", display: "flex", flexDirection: "column", gap: 8 }}>

                  {/* START YEAR */}
                  <input
                    type="number"
                    placeholder="Start year"
                    value={active.gte}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (isNaN(val)) return;

                      setOptAggs(prev => ({
                        ...(prev || {}),
                        [fieldKey]: {
                          ...(prev?.[fieldKey] || {}),
                          gte: val
                        }
                      }));
                    }}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #ddd",
                      borderRadius: 4
                    }}
                  />

                  {/* END YEAR */}
                  <input
                    type="number"
                    placeholder="End year"
                    value={active.lte}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (isNaN(val)) return;

                      setOptAggs(prev => ({
                        ...(prev || {}),
                        [fieldKey]: {
                          ...(prev?.[fieldKey] || {}),
                          lte: val
                        }
                      }));
                    }}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #ddd",
                      borderRadius: 4
                    }}
                  />

                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {active.gte} → {active.lte}
                  </div>

                  <div>
                  {filterError && (
                    <Message negative size="small">
                      {filterError}
                    </Message>
                  )}
                  </div>

                </div>
              )}
            </Menu.Item>
          );
        }

        /**
         * =========================
         * NORMAL SCHEMA FILTERS
         * =========================
         */

        if (!schema) return null;

        const activeValues = optAggs?.[fieldKey] || [];

        const dropdownOptions = schema.map(opt => {
          const count = bucketMap.get(opt.value) || 0;

          return {
            key: opt.value,
            value: opt.value,
            text: (
              <span>
                {opt.label}
                <Label horizontal basic size="mini" style={{ marginLeft: 8 }}>
                  <Icon name="file" />
                  {count}
                </Label>
              </span>
            )
          };
        });

        return (
          <Menu.Item key={i}>

            <div
              onClick={() => toggleSection(section.name)}
              style={{
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              {section.name}
              <Icon name={isOpen ? "chevron up" : "chevron down"} />
            </div>

            {isOpen && (
              <div style={{ marginTop: 8 }}>

                {fieldKey === "voice_type" ? (
                  <Dropdown
                    placeholder="Select voice types"
                    fluid
                    multiple
                    selection
                    options={dropdownOptions}
                    value={Array.isArray(activeValues) ? activeValues : []}
                    onChange={(e, { value }) => {
                      setOptAggs(prev => ({
                        ...(prev || {}),
                        [fieldKey]: value.length ? value : undefined
                      }));
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6
                    }}
                  >
                    {schema.map(opt => {

                      const isActive = Array.isArray(activeValues)
                        ? activeValues.includes(opt.value)
                        : activeValues === opt.value;

                      const count = bucketMap.get(opt.value) || 0;

                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            const next = toggleValue(
                              optAggs || {},
                              fieldKey,
                              opt.value
                            );

                            setOptAggs(next);
                          }}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 6,
                            border: "1px solid #ddd",
                            background: isActive ? "#2185d0" : "white",
                            color: isActive ? "white" : "black",
                            cursor: "pointer",
                            textAlign: "left"
                          }}
                        >
                          {opt.label}

                          <Label
                            horizontal
                            basic
                            size="mini"
                            style={{ marginLeft: 8 }}
                          >
                            <Icon name="file" />
                            {count}
                          </Label>
                        </button>
                      );
                    })}
                  </div>
                )}

              </div>
            )}

          </Menu.Item>
        );
      })}
    </Menu>
  );
};